/**
 * CGFinder Form Manager
 * Handles form discovery, instance management, and form lifecycle
 */
class CGFinderFormManager {
  static FORM_CONTAINERS = [
    'form',
    '.vehicle-selector-modal__form',
    '.vehicle-banner-form',
    '.easysearch-widget',
    '.collection-easysearch-custom',
  ];

  static VARIANT_DETECTION = {
    banner: ['.vehicle-banner-section', '.vehicle-banner-form', '#vehicle-banner-form'],
    modal: ['.vehicle-selector-modal', '.vehicle-selector-modal__form'],
    collection: ['.collection-easysearch-custom', '.collection', '[class*="collection"]'],
    header: ['.header', 'header'],
  };

  static SELECTORS = {
    year: '.cgfinder-year-select',
    model: '.cgfinder-model-select',
    details: '.cgfinder-details-select',
    category: '.cgfinder-category-select',
    clearButton: '.easysearch-btn-clean',
    searchButton: '.easysearch-btn-search',
    saveButton: '.vehicle-selector-modal__save',
    headerLabel: '.header__label-search.button--white',
  };

  static PLACEHOLDERS = {
    year: { default: 'Year', banner: 'Choose Year', collection: 'Choose Year' },
    model: { default: 'Model', banner: 'Choose Model', collection: 'Choose Model' },
    details: { default: 'Details', banner: 'Choose Details', collection: 'Choose Details' },
    category: { default: 'Category', banner: 'Choose Category', collection: 'Choose Category' },
    option: 'Choose Option',
  };

  constructor() {
    this.instances = new Map();
    this.domCache = new Map();
  }

  /**
   * Discover all search forms on the page and register them
   */
  discoverForms() {
    const yearSelects = document.querySelectorAll(CGFinderFormManager.SELECTORS.year);

    yearSelects.forEach((yearSelect, index) => {
      const form = this.findFormContainer(yearSelect);

      if (form) {
        const instance = this.createFormInstance(form, yearSelect, index);
        this.instances.set(instance.formId, instance);
        this.initializeFormInstance(instance);

      } else {
        console.warn(`No form container found for year select at index ${index}`, yearSelect);
      }
    });

    return this.instances;
  }

  /**
   * Find the appropriate form container for a year select element
   */
  findFormContainer(yearSelect) {
    for (const selector of CGFinderFormManager.FORM_CONTAINERS) {
      const container = yearSelect.closest(selector);
      if (container) return container;
    }
    return null;
  }

  /**
   * Create a form instance object
   */
  createFormInstance(form, yearSelect, index) {
    const formId = form.id || `cgfinder-form-${index}`;
    if (!form.id) form.id = formId;

    return {
      formId,
      form,
      yearSelect,
      modelSelect: form.querySelector(CGFinderFormManager.SELECTORS.model),
      detailsSelect: form.querySelector(CGFinderFormManager.SELECTORS.details),
      categorySelect: form.querySelector(CGFinderFormManager.SELECTORS.category),
      variant: this.detectVariant(form),
      isFormElement: form.tagName.toLowerCase() === 'form'
    };
  }

  /**
   * Initialize a form instance with proper state
   */
  initializeFormInstance(instance) {
    // Disable model and details selects initially
    if (instance.modelSelect) {
      instance.modelSelect.disabled = true;
    }
    if (instance.detailsSelect) {
      instance.detailsSelect.disabled = true;
    }

    // Category select should be enabled if it exists (for banner variant)
    if (instance.categorySelect && instance.variant === 'banner') {
      instance.categorySelect.disabled = false;
    }

    // Add data attributes for easier identification
    instance.form.setAttribute('data-cgfinder-form', instance.formId);
    instance.form.setAttribute('data-cgfinder-variant', instance.variant);

    // Add unique identifiers to elements if they don't have IDs
    this.ensureUniqueIdentifiers(instance);
  }

  /**
   * Ensure form elements have unique identifiers for debugging
   */
  ensureUniqueIdentifiers(instance) {
    const timestamp = Date.now();

    if (instance.yearSelect && !instance.yearSelect.id) {
      instance.yearSelect.setAttribute('data-cgfinder-element', `year-${instance.formId}-${timestamp}`);
    }
    if (instance.modelSelect && !instance.modelSelect.id) {
      instance.modelSelect.setAttribute('data-cgfinder-element', `model-${instance.formId}-${timestamp}`);
    }
    if (instance.detailsSelect && !instance.detailsSelect.id) {
      instance.detailsSelect.setAttribute('data-cgfinder-element', `details-${instance.formId}-${timestamp}`);
    }
    if (instance.categorySelect && !instance.categorySelect.id) {
      instance.categorySelect.setAttribute('data-cgfinder-element', `category-${instance.formId}-${timestamp}`);
    }
  }

  /**
   * Detect the variant type of a form (banner, modal, collection, etc.)
   */
  detectVariant(form) {
    for (const [variant, selectors] of Object.entries(CGFinderFormManager.VARIANT_DETECTION)) {
      if (this.matchesVariantSelectors(form, selectors)) {
        return variant;
      }
    }
    return 'default';
  }

  /**
   * Check if form matches any of the variant selectors
   */
  matchesVariantSelectors(form, selectors) {
    return selectors.some(selector => {
      if (selector.startsWith('#')) {
        return form.id === selector.substring(1);
      }
      if (selector.startsWith('.')) {
        return form.classList.contains(selector.substring(1)) || form.closest(selector);
      }
      return form.closest(selector);
    });
  }

  /**
   * Get form instance by element
   */
  getInstanceByElement(element) {
    if (!element) return null;

    for (const [formId, instance] of this.instances) {
      if (instance.form.contains(element)) {
        return instance;
      }
    }
    return null;
  }

  /**
   * Get form instance by ID
   */
  getInstance(formId) {
    return this.instances.get(formId);
  }

  /**
   * Get all form instances
   */
  getAllInstances() {
    return this.instances;
  }

  /**
   * Get appropriate placeholder text based on variant
   */
  getPlaceholder(type, variant) {
    const placeholders = CGFinderFormManager.PLACEHOLDERS[type];
    return placeholders[variant] || placeholders.default;
  }

  /**
   * Reset form selections to initial state
   */
  resetFormSelections(instance) {
    const resetConfig = [
      { element: instance.yearSelect, enabled: true },
      { element: instance.modelSelect, enabled: false },
      { element: instance.detailsSelect, enabled: false },
      { element: instance.categorySelect, enabled: true },
    ];

    resetConfig.forEach(({ element, enabled }) => {
      if (element) {
        this.resetSelectToFirstOption(element);
        this.toggleSelectElement(element, enabled);
      }
    });
  }

  /**
   * Reset select element to first option without clearing options
   */
  resetSelectToFirstOption(selectElement) {
    if (!selectElement) return;

    selectElement.selectedIndex = 0;
    selectElement.value = '';
  }

  /**
   * Toggle specific select element enabled/disabled state
   */
  toggleSelectElement(selectElement, enabled) {
    if (selectElement) {
      selectElement.disabled = !enabled;
    }
  }

  /**
   * Reset cascading select elements
   */
  resetCascadingSelects(instance, selectTypes) {
    selectTypes.forEach(type => {
      const element = instance[`${type}Select`];
      if (element) {
        this.resetSelectElement(element);
      }
    });
  }

  /**
   * Reset specific select element to default state
   */
  resetSelectElement(selectElement, placeholder = 'Choose Option') {
    if (!selectElement) return;

    selectElement.innerHTML = `<option value="">${placeholder}</option>`;
    selectElement.disabled = true;
    selectElement.value = '';
  }

  /**
   * Get selection for a specific instance
   */
  getInstanceSelection(instance) {
    const detailsId = instance.detailsSelect?.value || null;

    // Get metaobjectId and searchField from the selected details option's data attributes
    let metaobjectId = null;
    let searchField = null;
    let hasDetailsSelected = false;

    if (instance.detailsSelect && instance.detailsSelect.selectedIndex > 0 && detailsId) {
      hasDetailsSelected = true;
      const selectedOption = instance.detailsSelect.options[instance.detailsSelect.selectedIndex];
      if (selectedOption) {
        // Get metaobjectId from data-filter-data attribute
        metaobjectId = selectedOption.getAttribute('data-filter-data') || detailsId;
        // Get searchField from data-search-field attribute
        searchField = selectedOption.getAttribute('data-search-field') || null;
      }
    }

    // Fallback to localStorage ONLY if details was selected but attributes were missing
    // Do NOT fallback when no details is selected — that would return stale data
    if (hasDetailsSelected && (!metaobjectId || !searchField)) {
      try {
        const storedData = JSON.parse(localStorage.getItem('cgfinder-selections') || '{}');
        if (!metaobjectId) {
          metaobjectId = storedData['cgfinder-metaobjectId'] || detailsId;
        }
        if (!searchField) {
          searchField = storedData['cgfinder-search-field'] || 'vehicle.list1';
        }
      } catch (e) {
        // Keep defaults
      }
    }

    // Default searchField if still not resolved
    if (!searchField) searchField = 'vehicle.list1';

    return {
      formId: instance.formId,
      variant: instance.variant,
      year: {
        id: instance.yearSelect?.value || null,
        text: instance.yearSelect?.options[instance.yearSelect.selectedIndex]?.text || null
      },
      model: {
        id: instance.modelSelect?.value || null,
        text: instance.modelSelect?.options[instance.modelSelect.selectedIndex]?.text || null
      },
      details: {
        id: detailsId,
        text: instance.detailsSelect?.options[instance.detailsSelect.selectedIndex]?.text || null
      },
      category: {
        id: instance.categorySelect?.value || null,
        text: instance.categorySelect?.options[instance.categorySelect.selectedIndex]?.text || null
      },
      // Include metaobjectId from data-filter-data attribute (this is the key fix!)
      metaobjectId: metaobjectId,
      // Include searchField from data-search-field attribute
      searchField: searchField
    };
  }

  /**
   * Clear selections for a specific form instance
   */
  clearFormInstance(instance) {
    if (!instance) return;

    this.resetFormSelections(instance);
    this.dispatchClearEvent(instance);
  }

  /**
   * Dispatch form clear event
   */
  dispatchClearEvent(instance) {
    document.dispatchEvent(new CustomEvent('cgfinder:formCleared', {
      detail: {
        formId: instance.formId,
        variant: instance.variant
      }
    }));
  }

  /**
   * Get debug info about all registered forms
   */
  getDebugInfo() {
    const info = {
      totalForms: this.instances.size,
      variants: {},
      forms: []
    };

    this.instances.forEach((instance, formId) => {
      const variant = instance.variant;
      if (!info.variants[variant]) {
        info.variants[variant] = 0;
      }
      info.variants[variant]++;

      info.forms.push({
        formId: formId,
        variant: variant,
        hasYear: !!instance.yearSelect,
        hasModel: !!instance.modelSelect,
        hasDetails: !!instance.detailsSelect,
        hasCategory: !!instance.categorySelect,
        currentSelection: this.getInstanceSelection(instance)
      });
    });

    return info;
  }

  /**
   * Add vehicle data as hidden inputs to form
   */
  addVehicleDataToForm(form, vehicleData) {
    // Remove existing hidden inputs
    const existingInputs = form.querySelectorAll('input[name^="vehicle_"]');
    existingInputs.forEach(input => input.remove());

    // Add new hidden inputs
    const hiddenInputs = [
      { name: 'vehicle_year', value: vehicleData.yearText },
      { name: 'vehicle_model', value: vehicleData.modelText },
      { name: 'vehicle_details', value: vehicleData.detailsText },
      { name: 'vehicle_filter', value: `${vehicleData.yearText}-${vehicleData.modelText}-${vehicleData.detailsText}`.toLowerCase().replace(/\s+/g, '-') }
    ];

    hiddenInputs.forEach(({ name, value }) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
  }

  /**
   * Get selected text from a select element
   */
  getSelectedText(selectElement) {
    return selectElement?.options[selectElement.selectedIndex]?.text;
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.CGFinderFormManager = CGFinderFormManager;
}